import React, { useState, useEffect, useCallback } from 'react';
import { DARK_PATTERNS } from '../data/algorithmData';
import { addXP, loadXP } from '../data/xp';
import { syncProgress } from '../api/public';
import ModuleSummary from '../components/ModuleSummary';
import { recordStart } from '../data/progress';

type Mode = 'intro' | 'defender' | 'result';

interface Round {
  pattern: typeof DARK_PATTERNS[0];
  spotted: boolean;
  timeLeft: number;
}

const TIME_PER_ROUND = 12; // seconds

export default function DataHeist() {
  const [mode, setMode] = useState<Mode>('intro');
  const [roundIndex, setRoundIndex] = useState(0);
  const [timeLeft, setTimeLeft] = useState(TIME_PER_ROUND);
  const [spotted, setSpotted] = useState(false);
  const [results, setResults] = useState<{ spotted: boolean; time: number }[]>([]);
  const [score, setScore] = useState(0);
  const [timerActive, setTimerActive] = useState(false);

  const currentPattern = DARK_PATTERNS[roundIndex];

  // Timer
  useEffect(() => {
    if (!timerActive || mode !== 'defender') return;
    if (timeLeft <= 0) {
      handleMiss();
      return;
    }
    const t = setTimeout(() => setTimeLeft(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [timeLeft, timerActive, mode]);

  function startGame() {
    setMode('defender');
    setRoundIndex(0);
    setTimeLeft(TIME_PER_ROUND);
    setSpotted(false);
    setResults([]);
    setScore(0);
    setTimerActive(true);
  }

  function handleSpot() {
    if (spotted) return;
    const timeTaken = TIME_PER_ROUND - timeLeft;
    const xpEarned = Math.max(10, 50 - timeTaken * 3);
    setSpotted(true);
    setTimerActive(false);
    setScore(s => s + xpEarned);
    setResults(prev => [...prev, { spotted: true, time: timeTaken }]);
  }

  function handleMiss() {
    setTimerActive(false);
    setSpotted(false);
    setResults(prev => [...prev, { spotted: false, time: TIME_PER_ROUND }]);
    goNext(false);
  }

  function goNext(didSpot: boolean) {
    if (roundIndex + 1 >= DARK_PATTERNS.length) {
      // finished all rounds
      const finalXP = results.filter(r => r.spotted).length * 25 + (didSpot ? 25 : 0);
      addXP(finalXP, 'data_heist');
      recordStart();
      syncProgress('data_heist', finalXP);
      setMode('result');
    } else {
      setRoundIndex(i => i + 1);
      setTimeLeft(TIME_PER_ROUND);
      setSpotted(false);
      setTimerActive(true);
    }
  }

  const timerPct = (timeLeft / TIME_PER_ROUND) * 100;
  const timerColor = timeLeft > 7 ? '#10b981' : timeLeft > 4 ? '#f59e0b' : '#f43f5e';

  // ── Intro ────────────────────────────────────────────────────────────────
  if (mode === 'intro') return (
    <div style={{ minHeight: '100vh', background: '#111827', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ maxWidth: 600, textAlign: 'center' }}>
        <div style={{ fontSize: 64, marginBottom: 16 }}>🕵️</div>
        <h1 style={{ fontSize: 36, fontWeight: 800, color: 'white', marginBottom: 12 }}>Data Heist</h1>
        <p style={{ fontSize: 17, color: '#9ca3af', lineHeight: 1.7, marginBottom: 20 }}>
          Companies use <strong style={{ color: '#fbbf24' }}>dark patterns</strong> — sneaky UI tricks designed to manipulate you into giving up your data.
        </p>
        <p style={{ fontSize: 17, color: '#9ca3af', lineHeight: 1.7, marginBottom: 28 }}>
          Your job: <strong style={{ color: '#34d399' }}>spot the dark pattern</strong> in each round before time runs out. The faster you spot it, the more XP you earn.
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 32, textAlign: 'left' }}>
          {[
            { icon: '⏱', label: '12 seconds', desc: 'per round' },
            { icon: '🎯', label: '6 rounds', desc: 'of dark patterns' },
            { icon: '🏅', label: 'Up to 150 XP', desc: 'based on speed' },
          ].map(item => (
            <div key={item.label} style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 12, padding: 16, border: '1px solid rgba(255,255,255,0.1)' }}>
              <div style={{ fontSize: 24, marginBottom: 6 }}>{item.icon}</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'white' }}>{item.label}</div>
              <div style={{ fontSize: 12, color: '#9ca3af' }}>{item.desc}</div>
            </div>
          ))}
        </div>

        <button onClick={startGame} style={{
          background: 'linear-gradient(135deg, #f59e0b, #d97706)',
          color: '#1c1917', border: 'none', borderRadius: 12,
          padding: '14px 40px', fontSize: 18, fontWeight: 800, cursor: 'pointer',
        }}>
          Start the heist →
        </button>
      </div>
    </div>
  );

  // ── Defender round ───────────────────────────────────────────────────────
  if (mode === 'defender') {
    return (
      <div style={{ minHeight: '100vh', background: '#111827', padding: '24px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        {/* Header */}
        <div style={{ width: '100%', maxWidth: 640, marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <span style={{ color: '#9ca3af', fontSize: 14 }}>Round {roundIndex + 1} / {DARK_PATTERNS.length}</span>
            <span style={{ color: '#fbbf24', fontWeight: 700, fontSize: 14 }}>🏅 {score} XP so far</span>
          </div>
          {/* Timer bar */}
          <div style={{ height: 8, background: 'rgba(255,255,255,0.1)', borderRadius: 4, overflow: 'hidden' }}>
            <div style={{
              width: `${timerPct}%`, height: '100%',
              background: timerColor,
              transition: 'width 1s linear, background 0.3s',
              borderRadius: 4,
            }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
            <span style={{ fontSize: 13, color: timerColor, fontWeight: 700 }}>{timeLeft}s</span>
          </div>
        </div>

        {/* Pattern card */}
        <div style={{ width: '100%', maxWidth: 640 }}>
          <div style={{ background: '#1f2937', borderRadius: 20, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.08)' }}>
            {/* Dark pattern "UI" mockup */}
            <div style={{ padding: '28px 28px 20px' }}>
              <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 16 }}>
                Difficulty: {'★'.repeat(currentPattern.difficulty)}{'☆'.repeat(3 - currentPattern.difficulty)}
              </div>

              {/* Simulated UI based on dark pattern */}
              <DarkPatternMockup id={currentPattern.id} />
            </div>

            {/* Action area */}
            {!spotted ? (
              <div style={{ padding: '0 28px 28px' }}>
                <p style={{ color: '#9ca3af', fontSize: 14, marginBottom: 16, lineHeight: 1.5 }}>
                  👆 Can you spot the dark pattern in the UI above? Click when you've found it.
                </p>
                <button onClick={handleSpot} style={{
                  width: '100%',
                  background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                  color: '#1c1917', border: 'none', borderRadius: 12,
                  padding: '14px', fontSize: 16, fontWeight: 800, cursor: 'pointer',
                }}>
                  🎯 I spotted it!
                </button>
              </div>
            ) : (
              <div style={{ padding: '0 28px 28px' }}>
                <div style={{ background: '#064e3b', border: '1px solid #6ee7b7', borderRadius: 12, padding: 20, marginBottom: 16 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#34d399', marginBottom: 6 }}>
                    ✓ Correct! Dark pattern identified: <strong>{currentPattern.name}</strong>
                  </div>
                  <p style={{ fontSize: 14, color: '#a7f3d0', lineHeight: 1.6, margin: 0 }}>
                    {currentPattern.description}
                  </p>
                </div>
                <button onClick={() => goNext(true)} style={{
                  width: '100%',
                  background: '#3730a3', color: 'white', border: 'none', borderRadius: 12,
                  padding: '12px', fontSize: 15, fontWeight: 700, cursor: 'pointer',
                }}>
                  {roundIndex + 1 < DARK_PATTERNS.length ? 'Next round →' : 'See results →'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Results ──────────────────────────────────────────────────────────────
  if (mode === 'result') {
    const spotted_count = results.filter(r => r.spotted).length;
    const pct = Math.round((spotted_count / DARK_PATTERNS.length) * 100);
    const xpState = loadXP();

    return (
      <div style={{ minHeight: '100vh', background: '#111827', padding: '48px 24px', textAlign: 'center' }}>
        <div style={{ maxWidth: 540, margin: '0 auto' }}>
          <div style={{ fontSize: 64, marginBottom: 16 }}>
            {pct >= 80 ? '🏆' : pct >= 50 ? '⭐' : '📚'}
          </div>
          <h1 style={{ fontSize: 32, fontWeight: 800, color: 'white', marginBottom: 8 }}>Heist complete!</h1>
          <p style={{ color: '#9ca3af', fontSize: 16, marginBottom: 28 }}>
            You spotted {spotted_count} of {DARK_PATTERNS.length} dark patterns ({pct}%)
          </p>

          <div style={{ display: 'flex', gap: 16, justifyContent: 'center', marginBottom: 32 }}>
            <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 16, padding: '16px 24px', border: '1px solid rgba(255,255,255,0.1)' }}>
              <div style={{ fontSize: 28, fontWeight: 800, color: '#fbbf24' }}>{score}</div>
              <div style={{ fontSize: 13, color: '#9ca3af' }}>XP Earned</div>
            </div>
            <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 16, padding: '16px 24px', border: '1px solid rgba(255,255,255,0.1)' }}>
              <div style={{ fontSize: 28, fontWeight: 800, color: '#34d399' }}>{spotted_count}/{DARK_PATTERNS.length}</div>
              <div style={{ fontSize: 13, color: '#9ca3af' }}>Spotted</div>
            </div>
            <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 16, padding: '16px 24px', border: '1px solid rgba(255,255,255,0.1)' }}>
              <div style={{ fontSize: 28, fontWeight: 800, color: '#818cf8' }}>Lv.{xpState.level}</div>
              <div style={{ fontSize: 13, color: '#9ca3af' }}>Current Level</div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
            <button onClick={startGame} style={{
              background: 'linear-gradient(135deg, #f59e0b, #d97706)',
              color: '#1c1917', border: 'none', borderRadius: 12,
              padding: '12px 24px', fontSize: 15, fontWeight: 700, cursor: 'pointer',
            }}>Play again</button>
          </div>
          <ModuleSummary moduleId="dataheist" xpEarned={score} score={pct} scoreLabel="Patterns Spotted" />
        </div>
      </div>
    );
  }

  return null;
}

// ── Dark Pattern Mockups ─────────────────────────────────────────────────────
function DarkPatternMockup({ id }: { id: string }) {
  const base = { fontFamily: 'inherit', fontSize: 14, color: '#e5e7eb' };

  if (id === 'dp1') return (
    <div style={{ background: '#374151', borderRadius: 12, padding: 20 }}>
      <p style={{ ...base, marginBottom: 16, color: '#f3f4f6', fontWeight: 600 }}>Create your free account</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 16 }}>
        <input placeholder="Email address" style={{ background: '#1f2937', border: '1px solid #4b5563', borderRadius: 8, padding: '10px 14px', color: '#f3f4f6', fontSize: 14, outline: 'none' }} readOnly />
        <input type="password" placeholder="Password" style={{ background: '#1f2937', border: '1px solid #4b5563', borderRadius: 8, padding: '10px 14px', color: '#f3f4f6', fontSize: 14, outline: 'none' }} readOnly />
      </div>
      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8, cursor: 'pointer' }}>
        <input type="checkbox" defaultChecked style={{ marginTop: 2, accentColor: '#6366f1' }} readOnly />
        <span style={{ fontSize: 13, color: '#9ca3af' }}>Send me weekly deals, offers and marketing emails from our partners</span>
      </label>
      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 16, cursor: 'pointer' }}>
        <input type="checkbox" style={{ marginTop: 2 }} readOnly />
        <span style={{ fontSize: 13, color: '#9ca3af' }}>I agree to the Terms of Service</span>
      </label>
      <button style={{ width: '100%', background: '#4f46e5', color: 'white', border: 'none', borderRadius: 8, padding: '10px', fontSize: 14, fontWeight: 600, cursor: 'default' }}>
        Create account
      </button>
    </div>
  );

  if (id === 'dp2') return (
    <div style={{ background: '#374151', borderRadius: 12, padding: 20, textAlign: 'center' }}>
      <p style={{ ...base, marginBottom: 6, fontWeight: 700, color: '#f3f4f6', fontSize: 16 }}>🎉 Save 40% on your order!</p>
      <p style={{ fontSize: 13, color: '#9ca3af', marginBottom: 20 }}>Sign up for our newsletter and get exclusive deals delivered to your inbox.</p>
      <button style={{ background: '#10b981', color: 'white', border: 'none', borderRadius: 8, padding: '10px 24px', fontSize: 14, fontWeight: 700, cursor: 'default', marginBottom: 12, width: '100%' }}>
        Yes! I want to save 40%
      </button>
      <button style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: 12, cursor: 'default', textDecoration: 'underline' }}>
        No thanks, I don't like saving money
      </button>
    </div>
  );

  if (id === 'dp3') return (
    <div style={{ background: '#374151', borderRadius: 12, padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <span style={{ color: '#f3f4f6', fontWeight: 600 }}>Account Settings</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #4b5563' }}>
        <span style={{ fontSize: 14, color: '#e5e7eb' }}>Subscribe to Premium</span>
        <button style={{ background: '#4f46e5', color: 'white', border: 'none', borderRadius: 6, padding: '4px 12px', fontSize: 12, cursor: 'default' }}>1 click</button>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #4b5563' }}>
        <span style={{ fontSize: 14, color: '#e5e7eb' }}>Cancel subscription</span>
        <span style={{ fontSize: 12, color: '#9ca3af' }}>Call 1-800-XXX-XXXX Mon–Fri 9am–5pm EST</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0' }}>
        <span style={{ fontSize: 14, color: '#e5e7eb' }}>Update billing</span>
        <button style={{ background: 'none', border: '1px solid #4b5563', color: '#9ca3af', borderRadius: 6, padding: '4px 12px', fontSize: 12, cursor: 'default' }}>Edit</button>
      </div>
    </div>
  );

  if (id === 'dp4') return (
    <div style={{ background: '#374151', borderRadius: 12, padding: 20 }}>
      <p style={{ ...base, marginBottom: 4, fontWeight: 600, color: '#f3f4f6' }}>You're all set! 🎉</p>
      <p style={{ fontSize: 13, color: '#9ca3af', marginBottom: 16 }}>Your order has been placed. We'll send updates to your email.</p>
      <div style={{ background: '#1f2937', borderRadius: 8, padding: 16, marginBottom: 4 }}>
        <p style={{ fontSize: 13, color: '#e5e7eb', marginBottom: 8, lineHeight: 1.5 }}>By completing your purchase you agree to our Privacy Policy and consent to receive marketing communications. You may also receive offers from our 200+ partner companies.</p>
        <p style={{ fontSize: '10px', color: '#374151', letterSpacing: '-0.02em' }}>To unsubscribe from marketing emails, click <span style={{ textDecoration: 'underline' }}>here</span>. To opt out of partner communications visit privacy.example.com/optout/partners/manage/preferences.</p>
      </div>
    </div>
  );

  if (id === 'dp5') return (
    <div style={{ background: '#374151', borderRadius: 12, padding: 20 }}>
      <p style={{ ...base, fontWeight: 700, color: '#f3f4f6', marginBottom: 4 }}>We value your privacy</p>
      <p style={{ fontSize: 13, color: '#9ca3af', marginBottom: 16, lineHeight: 1.5 }}>We use cookies to improve your experience and show you personalised content.</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <button style={{ background: '#4f46e5', color: 'white', border: 'none', borderRadius: 8, padding: '10px', fontSize: 14, fontWeight: 700, cursor: 'default' }}>
          Accept all cookies — Free access
        </button>
        <button style={{ background: 'none', border: '1px solid #4b5563', color: '#9ca3af', borderRadius: 8, padding: '10px', fontSize: 14, cursor: 'default' }}>
          Manage preferences — Free access
        </button>
        <button style={{ background: 'none', border: '1px solid #6b7280', color: '#6b7280', borderRadius: 8, padding: '10px', fontSize: 14, cursor: 'default' }}>
          Reject all — €4.99/month required
        </button>
      </div>
    </div>
  );

  if (id === 'dp6') return (
    <div style={{ background: '#374151', borderRadius: 12, padding: 20 }}>
      <p style={{ ...base, fontWeight: 600, color: '#f3f4f6', marginBottom: 12 }}>Privacy Settings</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {['Notifications', 'Account', 'Security', 'Display', 'Connected Apps', 'Privacy', 'Advanced'].map((item, i) => (
          <div key={item} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 10px', background: i === 5 ? '#1f2937' : 'transparent', borderRadius: 6 }}>
            <span style={{ fontSize: 13, color: i === 5 ? '#e5e7eb' : '#6b7280' }}>{item}</span>
            <span style={{ fontSize: 12, color: '#6b7280' }}>→</span>
          </div>
        ))}
      </div>
      <p style={{ fontSize: 11, color: '#6b7280', marginTop: 8 }}>
        Note: Data sharing preferences are under Privacy → Advanced → Third Parties → Advertising Partners → Manage
      </p>
    </div>
  );

  return <div style={{ color: '#9ca3af', padding: 20 }}>Pattern {id}</div>;
}
